/**
 * `/quiz/$quizId` — the QUIZ RUNNER (SCH Milestone A): the exam-taking
 * experience, one question at a time.
 *
 * Flow:
 *   1. Intro card → "Start test" calls `learning.attempts.start` (which
 *      enforces publish status, availability window, ACTIVE enrollment, and
 *      the attempt limit server-side). FORBIDDEN → friendly enroll/limit card.
 *   2. Active attempt: progress header ("Question 3 of 8" + thin bar +
 *      numbered pill nav), one question card per `kind`, Prev/Next footer.
 *   3. Every answer change AUTOSAVES via `attempts.saveAnswer` (≈400 ms
 *      debounce per question, serialized on one promise chain so writes land
 *      in order; flushed on navigation + before submit). `elapsedMs` is the
 *      accumulated time the learner spent ON that question.
 *   4. "Submit test" → ConfirmDialog ("You answered X of N") → flush pending
 *      saves → `attempts.submit` → navigate to `/result/$attemptId`.
 *
 * TIMER (SCH-AI-1b, §3.3c): `attempts.start` returns `timeLimitSeconds` +
 * `startedAt` (the attempt's server-side `started_at` — anchoring the deadline
 * to the SERVER clock, so a slow response doesn't gift extra seconds and a
 * resumed attempt counts from its true start). deadline = startedAt + limit;
 * the header renders icon + literal mm:ss with urgency tones from semantic
 * CSS-var tokens (>50 % default, ≤50 % `--color-warning`, ≤25 %
 * `--color-destructive`) — text always present, never color-only. At the
 * T-5m / T-1m / T-10s checkpoints the debounced autosave queue flushes (same
 * flush as pre-submit); at T-0 the attempt auto-submits exactly once with
 * `submitReason: 'timeout'`. If the server's lazy expiry closed the attempt
 * first (`saveAnswer` → CONFLICT 'This attempt is closed.' / `submit` →
 * 'already been submitted'), a banner announces "Time is up" and the learner
 * lands on the result. Untimed quizzes (`timeLimitSeconds` null) render no
 * countdown — unchanged behavior.
 *
 * Minimal chrome by design (no `<LearnShell>`): a slim sticky header — quiz
 * title left (optional `?title=` search param, falls back to "Quiz"), save
 * indicator + timer right — keeps the student focused on the test.
 */
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import { AlertCircle, Check, ChevronLeft, ChevronRight, Leaf, Timer } from 'lucide-react';
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardHeader,
  ConfirmDialog,
  ImageUploadField,
  Input,
  MathText,
  cn,
} from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { RequireAuth } from '../lib/auth.js';

// ---------------------------------------------------------------------------
// Local types + pure helpers
// ---------------------------------------------------------------------------

type StartResult = inferRouterOutputs<AppRouter>['learning']['attempts']['start'];
/** A question as the learner sees it — answer keys stripped server-side. */
type LearnerQuestion = StartResult['questions'][number];

interface AttemptSession {
  attemptId: string;
  questions: LearnerQuestion[];
  /** Quiz time limit in seconds; null = untimed (no countdown rendered). */
  timeLimitSeconds: number | null;
  /** Server-clock anchor — the attempt's `started_at` (ms since epoch). */
  startedAtMs: number;
}

/**
 * Question kinds, hardcoded locally — the SPA must NOT import
 * `@mangrove/core` (ADR-0058). Mirrors `QUESTION_KINDS` in
 * `@mangrove/core/learning`.
 */
const QUESTION_KINDS = [
  'mcq',
  'multi',
  'true_false',
  'fill_blank',
  'short_answer',
  'numeric',
  'essay',
] as const;
type QuestionKind = (typeof QUESTION_KINDS)[number];

/** Server DTOs type `kind` as `string`; unknown kinds degrade to a text input. */
function kindOf(raw: string): QuestionKind {
  return (QUESTION_KINDS as readonly string[]).includes(raw)
    ? (raw as QuestionKind)
    : 'short_answer';
}

/**
 * The image attached to a question prompt (`promptImageObjectId`) or an option
 * (`imageObjectId`), read DEFENSIVELY off the DTO. P-SCH-* adds these fields to
 * the learner DTOs (`LearnerQuestionDto` + its options) — until the regenerated
 * `@mangrove/api-contract` rollup lands they aren't in the inferred type (and a
 * stale api omits them at runtime), so we probe by name and ignore anything but
 * a non-empty string. Same defensive shape as `startedAtMsOf` above. Returns
 * the `file_objects` id, or null when there's no image.
 */
function imageObjectIdOf(
  carrier: unknown,
  field: 'promptImageObjectId' | 'imageObjectId',
): string | null {
  if (carrier && typeof carrier === 'object' && field in carrier) {
    const raw = (carrier as Record<string, unknown>)[field];
    if (typeof raw === 'string' && raw !== '') return raw;
  }
  return null;
}

/** Local (pre-save) answer state for one question. */
interface LocalAnswer {
  selectedOptionIds: string[];
  /** fill_blank / short_answer / essay text. */
  text: string;
  /** numeric kind: raw input string so in-between states ("3.", "-") survive. */
  numericRaw: string;
  /**
   * short_answer / essay: the `file_objects` id of a photo of handwritten work
   * the learner attached (P-SCH-D), or null. Persisted onto
   * `attempt_answers.answerImageObjectId`.
   */
  answerImageObjectId: string | null;
}

const EMPTY_ANSWER: LocalAnswer = {
  selectedOptionIds: [],
  text: '',
  numericRaw: '',
  answerImageObjectId: null,
};

/**
 * One already-saved answer as `attempts.start` hands it back on a RESUME
 * (P-SCH-F). Read defensively off the response (a stale api omits `savedAnswers`
 * entirely) — mirrors the `startedAt` posture — so it's declared locally rather
 * than inferred from the contract.
 */
interface SavedAnswerWire {
  questionId: string;
  selectedOptionIds: string[] | null;
  answerText: string | null;
  answerNumeric: number | null;
  answerImageObjectId: string | null;
  elapsedMs: number | null;
}

const SAVE_DEBOUNCE_MS = 400;

/** §3.3c checkpoint flushes — seconds remaining at T-5m / T-1m / T-10s. */
const TIMER_FLUSH_CHECKPOINTS = [300, 60, 10] as const;

/** How long the "Time is up" banner shows before routing to the result. */
const CLOSED_BANNER_MS = 1800;

function isAnswered(kind: QuestionKind, a: LocalAnswer): boolean {
  switch (kind) {
    case 'mcq':
    case 'multi':
    case 'true_false':
      return a.selectedOptionIds.length > 0;
    case 'numeric':
      return Number.isFinite(Number.parseFloat(a.numericRaw));
    case 'short_answer':
    case 'essay':
      // An attached photo of handwritten work counts as answered on its own.
      return a.text.trim() !== '' || a.answerImageObjectId != null;
    default:
      return a.text.trim() !== '';
  }
}

/** Map the local answer to the `attempts.saveAnswer` input for this kind. */
function buildSavePayload(
  attemptId: string,
  question: LearnerQuestion,
  a: LocalAnswer,
  elapsedMs: number,
): {
  attemptId: string;
  questionId: string;
  selectedOptionIds?: string[];
  answerText?: string;
  answerNumeric?: number | null;
  answerImageObjectId?: string;
  elapsedMs: number;
} {
  const kind = kindOf(question.kind);
  const base = {
    attemptId,
    questionId: question.id,
    elapsedMs: Math.max(0, Math.round(elapsedMs)),
  };
  if (kind === 'mcq' || kind === 'multi' || kind === 'true_false') {
    return { ...base, selectedOptionIds: a.selectedOptionIds };
  }
  if (kind === 'numeric') {
    const parsed = Number.parseFloat(a.numericRaw);
    return { ...base, answerNumeric: Number.isFinite(parsed) ? parsed : null };
  }
  // short_answer / essay / fill_blank / unknown → text, plus any attached
  // handwritten-work photo (short_answer / essay only ever set it).
  return {
    ...base,
    answerText: a.text,
    ...(a.answerImageObjectId != null ? { answerImageObjectId: a.answerImageObjectId } : {}),
  };
}

/**
 * SERVER-clock anchor for the countdown (§3.3c): `attempts.start` returns the
 * attempt's `started_at` as an ISO string. Read defensively — until the
 * regenerated `@mangrove/api-contract` rollup lands the field isn't in the
 * inferred type (and a stale api would omit it at runtime) — falling back to
 * receipt time, the pre-1b behavior.
 */
function startedAtMsOf(data: StartResult): number {
  const raw = (data as { startedAt?: unknown }).startedAt;
  if (typeof raw === 'string') {
    const ms = Date.parse(raw);
    if (Number.isFinite(ms)) return ms;
  }
  return Date.now();
}

/** mm:ss (h:mm:ss past an hour). */
function formatClock(totalSeconds: number): string {
  const s = Math.max(0, totalSeconds);
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Best-effort tRPC error code (`FORBIDDEN`, `BAD_REQUEST`, …) from an unknown error. */
function errorCodeOf(err: unknown): string | null {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === 'object' && 'code' in data) {
      const code = (data as { code?: unknown }).code;
      if (typeof code === 'string') return code;
    }
  }
  return null;
}

function messageOf(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string' && m !== '') return m;
  }
  return 'Something went wrong. Please try again.';
}

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export interface QuizSearch {
  /** Optional display title passed by the course screen (cosmetic only). */
  title?: string;
}

export const Route = createFileRoute('/quiz/$quizId')({
  validateSearch: (search: Record<string, unknown>): QuizSearch => {
    const title =
      typeof search.title === 'string' && search.title !== '' ? search.title : undefined;
    return title === undefined ? {} : { title };
  },
  component: QuizRunnerPage,
});

function QuizRunnerPage(): React.JSX.Element {
  return (
    <RequireAuth>
      <QuizRunner />
    </RequireAuth>
  );
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

function QuizRunner(): React.JSX.Element {
  const { quizId } = Route.useParams();
  const { title } = Route.useSearch();
  const navigate = useNavigate();

  const start = trpc.learning.attempts.start.useMutation();
  const saveAnswer = trpc.learning.attempts.saveAnswer.useMutation();
  const submit = trpc.learning.attempts.submit.useMutation();
  const utils = trpc.useUtils();

  const [session, setSession] = React.useState<AttemptSession | null>(null);
  const [index, setIndex] = React.useState(0);
  const [answers, setAnswers] = React.useState<Record<string, LocalAnswer>>({});
  const [saveState, setSaveState] = React.useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [finishing, setFinishing] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = React.useState<number | null>(null);
  /** Count-UP elapsed time, shown when the quiz has NO time limit so a timer is
   *  ALWAYS visible (Ishraf v1 QA). Ticks for every active attempt. */
  const [elapsedSeconds, setElapsedSeconds] = React.useState(0);
  /** §3.3c closed-attempt contract: server already auto-submitted (timeout). */
  const [attemptClosed, setAttemptClosed] = React.useState(false);
  /** P-SCH-N anti-cheat: shown after the first tab-switch; the 2nd auto-submits. */
  const [tabSwitchWarning, setTabSwitchWarning] = React.useState(false);

  // Render-synced mirrors so debounced/interval closures always read fresh state.
  const sessionRef = React.useRef<AttemptSession | null>(null);
  sessionRef.current = session;
  const answersRef = React.useRef(answers);
  answersRef.current = answers;
  const indexRef = React.useRef(index);
  indexRef.current = index;

  // Autosave plumbing: per-question debounce timers + dirty set + ONE promise
  // chain so saves land on the server in the order they were made.
  const saveTimersRef = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const dirtyRef = React.useRef<Set<string>>(new Set());
  const saveChainRef = React.useRef<Promise<void>>(Promise.resolve());
  const saveFailedRef = React.useRef(false);

  // Per-question time tracking (`elapsedMs`).
  const elapsedRef = React.useRef<Map<string, number>>(new Map());
  const enteredAtRef = React.useRef<number>(Date.now());

  // Submit re-entrancy guard (covers double-click AND timeout racing manual).
  const submittingRef = React.useRef(false);
  // Timer one-shot guards — refs so a StrictMode remount or a 500 ms re-tick
  // can never double-fire the auto-submit or a checkpoint flush.
  const timeoutFiredRef = React.useRef(false);
  // P-SCH-N anti-cheat: count of times the learner left the tab mid-attempt.
  const tabSwitchCountRef = React.useRef(0);
  const firedCheckpointsRef = React.useRef<Set<number>>(new Set());
  const attemptClosedRef = React.useRef(false);
  const closeNavTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    const timers = saveTimersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      if (closeNavTimerRef.current != null) clearTimeout(closeNavTimerRef.current);
    };
  }, []);

  /** Time spent on a question: banked total + live delta if it's on screen. */
  const elapsedMsOf = (questionId: string): number => {
    const banked = elapsedRef.current.get(questionId) ?? 0;
    const onScreenId = sessionRef.current?.questions[indexRef.current]?.id;
    return banked + (onScreenId === questionId ? Date.now() - enteredAtRef.current : 0);
  };

  /**
   * §3.3c closed-attempt contract: the server's lazy expiry already
   * auto-submitted this attempt (`saveAnswer` → CONFLICT 'This attempt is
   * closed.', or `submit` → 'already been submitted'). Whatever landed before
   * the deadline is graded — stop the timer + autosaves, tell the learner,
   * then route to the result. Idempotent (ref-guarded): queued saves all
   * CONFLICTing at once collapse into one banner + one navigation.
   */
  const handleAttemptClosed = (attemptId: string): void => {
    if (attemptClosedRef.current) return;
    attemptClosedRef.current = true;
    submittingRef.current = true; // no manual/timeout submit may race past this
    setAttemptClosed(true); // tears down the countdown interval + shows the banner
    setRemainingSeconds(0);
    setFinishing(true);
    setConfirmOpen(false);
    closeNavTimerRef.current = setTimeout(() => {
      void navigate({ to: '/result/$attemptId', params: { attemptId }, replace: true });
    }, CLOSED_BANNER_MS);
  };

  /** Persist one question's answer now (no-op unless dirty). Serialized + retried-on-next-flush. */
  const flushQuestion = (questionId: string): Promise<void> => {
    const s = sessionRef.current;
    if (!s || attemptClosedRef.current || !dirtyRef.current.has(questionId)) {
      return saveChainRef.current;
    }
    dirtyRef.current.delete(questionId);
    const timer = saveTimersRef.current.get(questionId);
    if (timer !== undefined) {
      clearTimeout(timer);
      saveTimersRef.current.delete(questionId);
    }
    const question = s.questions.find((q) => q.id === questionId);
    if (!question) return saveChainRef.current;
    const payload = buildSavePayload(
      s.attemptId,
      question,
      answersRef.current[questionId] ?? EMPTY_ANSWER,
      elapsedMsOf(questionId),
    );
    setSaveState('saving');
    const run = saveChainRef.current.then(async () => {
      try {
        await saveAnswer.mutateAsync(payload);
        // The photo preview may have asked for this image before this save wrote
        // its id onto the answer, and cached the empty reply. Ask again now.
        if (payload.answerImageObjectId != null) {
          void utils.learning.attempts.getAnswerImage.invalidate({
            attemptId: s.attemptId,
            questionId,
          });
        }
        // More dirty questions queued → still effectively saving; else saved.
        setSaveState(dirtyRef.current.size > 0 ? 'saving' : 'saved');
      } catch (err) {
        if (errorCodeOf(err) === 'CONFLICT') {
          // 'This attempt is closed.' — the server timed it out and graded
          // what landed. Don't re-queue (it would CONFLICT forever).
          handleAttemptClosed(s.attemptId);
          return;
        }
        // Re-mark dirty so the next flush (navigation/submit) retries it.
        saveFailedRef.current = true;
        dirtyRef.current.add(questionId);
        setSaveState('error');
      }
    });
    saveChainRef.current = run;
    return run;
  };

  /** Flush every dirty question; resolves `true` when all saves landed. */
  const flushAll = async (): Promise<boolean> => {
    saveFailedRef.current = false;
    for (const id of [...dirtyRef.current]) void flushQuestion(id);
    await saveChainRef.current;
    return !saveFailedRef.current;
  };
  // Render-synced mirror so the countdown interval always flushes via the
  // freshest closure (same pattern as `handleSubmitRef` below).
  const flushAllRef = React.useRef(flushAll);
  flushAllRef.current = flushAll;

  const markAnswer = (questionId: string, patch: Partial<LocalAnswer>): void => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...(prev[questionId] ?? EMPTY_ANSWER), ...patch },
    }));
    dirtyRef.current.add(questionId);
    const existing = saveTimersRef.current.get(questionId);
    if (existing !== undefined) clearTimeout(existing);
    saveTimersRef.current.set(
      questionId,
      setTimeout(() => {
        void flushQuestion(questionId);
      }, SAVE_DEBOUNCE_MS),
    );
  };

  /** Navigate between questions: bank elapsed time + flush the one we leave. */
  const goTo = (next: number): void => {
    const s = sessionRef.current;
    if (!s) return;
    const leaving = s.questions[indexRef.current];
    if (leaving) {
      elapsedRef.current.set(leaving.id, elapsedMsOf(leaving.id));
      void flushQuestion(leaving.id);
    }
    enteredAtRef.current = Date.now();
    setIndex(Math.min(Math.max(next, 0), Math.max(0, s.questions.length - 1)));
  };

  const handleStart = (): void => {
    start.mutate(
      { quizId },
      {
        onSuccess: (data) => {
          enteredAtRef.current = Date.now();
          // P-SCH-F RESUME: when `start` reuses an in-progress attempt it returns
          // the answers already saved (empty on a fresh start). Rehydrate both the
          // visible answer state AND the per-question elapsed-time bank so a
          // reload / flap / device-switch drops the learner back exactly where they
          // were. Read defensively (a stale api omits the field) — same posture as
          // `startedAt`/`startedAtMsOf`.
          const saved = (data as { savedAnswers?: SavedAnswerWire[] }).savedAnswers ?? [];
          if (saved.length > 0) {
            const hydrated: Record<string, LocalAnswer> = {};
            for (const sa of saved) {
              hydrated[sa.questionId] = {
                selectedOptionIds: sa.selectedOptionIds ?? [],
                text: sa.answerText ?? '',
                numericRaw: sa.answerNumeric != null ? String(sa.answerNumeric) : '',
                answerImageObjectId: sa.answerImageObjectId ?? null,
              };
              if (sa.elapsedMs != null) elapsedRef.current.set(sa.questionId, sa.elapsedMs);
            }
            setAnswers(hydrated);
          }
          setSession({
            attemptId: data.attemptId,
            questions: data.questions,
            timeLimitSeconds: data.timeLimitSeconds,
            startedAtMs: startedAtMsOf(data),
          });
        },
      },
    );
  };

  const handleSubmit = async (reason: 'manual' | 'timeout' | 'tab_switch'): Promise<void> => {
    const s = sessionRef.current;
    if (!s || submittingRef.current) return;
    submittingRef.current = true;
    setFinishing(true);
    setSubmitError(null);
    try {
      const saved = await flushAll();
      // A CONFLICT during the flush means the server closed the attempt —
      // `handleAttemptClosed` is already banner-ing + routing to the result.
      if (attemptClosedRef.current) return;
      if (!saved && reason === 'manual') {
        // Manual submits insist on a clean flush; at T-0 we submit anyway —
        // the server grades whatever landed (§3.3c).
        setSubmitError('Some answers could not be saved. Check your connection and try again.');
        submittingRef.current = false;
        setFinishing(false);
        return;
      }
      await submit.mutateAsync({ attemptId: s.attemptId, submitReason: reason });
      void navigate({
        to: '/result/$attemptId',
        params: { attemptId: s.attemptId },
        replace: true,
      });
    } catch (err) {
      if (
        errorCodeOf(err) === 'BAD_REQUEST' &&
        messageOf(err).toLowerCase().includes('already been submitted')
      ) {
        // The server's lazy expiry beat us to it — the graded result exists.
        handleAttemptClosed(s.attemptId);
        return;
      }
      setSubmitError(messageOf(err));
      submittingRef.current = false;
      setFinishing(false);
    }
  };
  const handleSubmitRef = React.useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  // Live countdown (§3.3c): deadline anchored to the SERVER `started_at`. At
  // the T-5m/T-1m/T-10s checkpoints the debounced autosave queue flushes (so
  // a timeout grades fresh answers); at T-0 the attempt auto-submits exactly
  // once (`timeoutFiredRef` — StrictMode-remount and re-tick safe). Interval
  // tears down on unmount and when the closed-attempt banner takes over.
  React.useEffect(() => {
    if (!session || session.timeLimitSeconds == null || attemptClosed) return;
    const deadline = session.startedAtMs + session.timeLimitSeconds * 1000;
    const tick = (): void => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemainingSeconds(remaining);
      if (remaining > 0) {
        for (const checkpoint of TIMER_FLUSH_CHECKPOINTS) {
          if (remaining <= checkpoint && !firedCheckpointsRef.current.has(checkpoint)) {
            firedCheckpointsRef.current.add(checkpoint);
            void flushAllRef.current();
          }
        }
        return;
      }
      if (!timeoutFiredRef.current) {
        timeoutFiredRef.current = true;
        void handleSubmitRef.current('timeout');
      }
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [session, attemptClosed]);

  // Count-UP elapsed timer for UNTIMED quizzes — so a timer is ALWAYS on screen.
  // Anchored to the server `started_at` (survives reload/resume). For TIMED
  // quizzes the countdown above owns the display, so this no-ops there.
  React.useEffect(() => {
    if (!session || attemptClosed || session.timeLimitSeconds != null) return;
    const tick = (): void =>
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - session.startedAtMs) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [session, attemptClosed]);

  // P-SCH-N anti-cheat: leaving the tab mid-attempt is recorded. The FIRST
  // departure shows a warning banner; the SECOND auto-submits the attempt with
  // `submitReason: 'tab_switch'` (the server enum already accepts it). Only armed
  // while an attempt is genuinely in progress — never on the result/closed view.
  React.useEffect(() => {
    if (!session || attemptClosed) return;
    const onHidden = (): void => {
      if (document.visibilityState !== 'hidden') return;
      if (submittingRef.current || attemptClosedRef.current) return;
      tabSwitchCountRef.current += 1;
      if (tabSwitchCountRef.current >= 2) {
        void handleSubmitRef.current('tab_switch');
      } else {
        setTabSwitchWarning(true);
      }
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, [session, attemptClosed]);

  // ----- Derived view state ------------------------------------------------

  const questions = session?.questions ?? [];
  const total = questions.length;
  const question = questions[index];
  const answeredCount = questions.filter((q) =>
    isAnswered(kindOf(q.kind), answers[q.id] ?? EMPTY_ANSWER),
  ).length;
  const isLast = index === total - 1;

  // Urgency tones from semantic CSS-var tokens (never hardcoded colors); the
  // literal mm:ss text is always present — color is never the only signal.
  const timeLimitSeconds = session?.timeLimitSeconds ?? null;
  // A prominent, tone-aware countdown pill (Ishraf v1 QA: the timer must read at
  // a glance). The literal mm:ss is ALWAYS present — color is never the only
  // urgency signal. Semantic CSS-var tokens only, never hardcoded colors.
  const timerUrgency =
    remainingSeconds != null && timeLimitSeconds != null && timeLimitSeconds > 0
      ? remainingSeconds <= timeLimitSeconds * 0.25
        ? 'danger'
        : remainingSeconds <= timeLimitSeconds * 0.5
          ? 'warn'
          : 'normal'
      : 'normal';
  const timerPill =
    timerUrgency === 'danger'
      ? 'border-destructive/40 bg-destructive/10 text-destructive'
      : timerUrgency === 'warn'
        ? 'border-[color:var(--color-warning)]/40 bg-[color:var(--color-warning)]/10 text-[color:var(--color-warning)]'
        : 'border-border bg-muted/50 text-foreground';

  // The ALWAYS-ON header timer: countdown for a TIMED quiz, else count-UP elapsed
  // (so a big, eye-catching clock is on screen for every quiz).
  const timed = timeLimitSeconds != null && timeLimitSeconds > 0;
  const timerValue = timed ? (remainingSeconds ?? timeLimitSeconds ?? 0) : elapsedSeconds;
  const timerClass = timed ? timerPill : 'border-primary/40 bg-primary/10 text-primary';
  const timerAria = timed
    ? `Time remaining ${formatClock(timerValue)}`
    : `Time elapsed ${formatClock(timerValue)}`;

  // Shared "open submit confirmation" — used by the always-visible header Submit
  // button AND the footer Submit on the last question.
  const openSubmitConfirm = (): void => {
    if (question) void flushQuestion(question.id);
    setSubmitError(null);
    setConfirmOpen(true);
  };

  return (
    <div className="flex min-h-dvh flex-col bg-gradient-to-b from-primary/5 via-background to-background">
      <header className="sticky top-0 z-40 border-b bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/75">
        <div className="mx-auto flex h-16 w-full max-w-3xl items-center justify-between gap-3 px-4">
          {/* LEFT — big, eye-catching timer (or the brand glyph pre-start) + title */}
          <div className="flex min-w-0 items-center gap-2.5">
            {session ? (
              <div className="flex shrink-0 flex-col items-start gap-0.5">
                <span
                  role="timer"
                  aria-label={timerAria}
                  data-testid="quiz-timer"
                  className={cn(
                    'flex items-center gap-2 rounded-xl border px-4 py-1.5 font-mono text-2xl font-extrabold tabular-nums shadow-sm transition-colors duration-300',
                    timerClass,
                    timed && timerUrgency === 'danger' && 'animate-pulse',
                  )}
                >
                  <Timer className="size-6" aria-hidden="true" />
                  {formatClock(timerValue)}
                </span>
                {/* SCH-I3-C7: state the limit explicitly under the timer. */}
                <span
                  className="pl-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                  data-testid="quiz-time-limit"
                >
                  {timed ? `Time limit: ${Math.round(timeLimitSeconds / 60)} min` : 'No time limit'}
                </span>
              </div>
            ) : (
              <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/80 shadow-sm">
                <Leaf className="size-4 text-primary-foreground" aria-hidden="true" />
              </span>
            )}
            <h1 className="hidden truncate text-sm font-semibold sm:block">{title ?? 'Quiz'}</h1>
          </div>
          {/* RIGHT — save state + an ALWAYS-VISIBLE Submit (reachable on every question) */}
          <div className="flex shrink-0 items-center gap-2.5">
            {session ? (
              <span
                aria-live="polite"
                className={cn(
                  // Saving/Saved chatter is desktop-only, but 'Save failed'
                  // MUST render at every width — a phone learner on a flaky
                  // connection otherwise keeps answering while nothing saves.
                  saveState === 'error'
                    ? 'inline text-xs font-medium text-destructive'
                    : 'hidden text-xs text-muted-foreground sm:inline',
                )}
              >
                {saveState === 'saving'
                  ? 'Saving…'
                  : saveState === 'saved'
                    ? 'Saved'
                    : saveState === 'error'
                      ? 'Save failed'
                      : null}
              </span>
            ) : null}
            {session && total > 0 ? (
              <Button
                className="press min-h-touch font-semibold"
                onClick={openSubmitConfirm}
                disabled={finishing}
                data-testid="quiz-submit-top"
              >
                {finishing ? 'Submitting…' : 'Submit quiz'}
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 py-6">
        {attemptClosed ? (
          <div
            role="alert"
            data-testid="time-up-banner"
            className="animate-fade-in mb-4 flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-4 py-3 text-sm font-medium"
          >
            <Timer className="size-4 shrink-0" aria-hidden="true" />
            Time is up — your answers were submitted. Taking you to your score…
          </div>
        ) : null}
        {tabSwitchWarning && !attemptClosed && session ? (
          <div
            role="alert"
            data-testid="tab-switch-warning"
            className="animate-fade-in mb-4 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              Leaving the test is recorded. Switch away again and your test will be submitted
              automatically.
            </span>
          </div>
        ) : null}
        {!session ? (
          start.error ? (
            <StartErrorCard error={start.error} onRetry={() => start.reset()} />
          ) : (
            <IntroCard
              quizId={quizId}
              title={title}
              starting={start.isPending}
              onStart={handleStart}
            />
          )
        ) : total === 0 ? (
          <CenteredCard
            title="This test has no questions yet"
            body="Check back soon — your instructor hasn't added any questions."
          />
        ) : question ? (
          <div className="flex flex-1 flex-col gap-5">
            {/* Progress */}
            <div className="flex flex-col gap-3">
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-sm font-medium">
                  Question {index + 1} of {total}
                </p>
                <p className="text-xs text-muted-foreground">{answeredCount} answered</p>
              </div>
              <div
                className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                aria-hidden="true"
              >
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${((index + 1) / total) * 100}%` }}
                />
              </div>
              <nav aria-label="Jump to question" className="flex flex-wrap gap-1.5">
                {questions.map((q, i) => {
                  const answered = isAnswered(kindOf(q.kind), answers[q.id] ?? EMPTY_ANSWER);
                  const current = i === index;
                  return (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => goTo(i)}
                      aria-current={current ? 'step' : undefined}
                      aria-label={`Question ${i + 1}${answered ? ' (answered)' : ''}`}
                      className={cn(
                        'press flex size-8 items-center justify-center rounded-[10px] border text-xs font-semibold tabular-nums transition-all duration-150',
                        // current = filled; answered = tinted + a check tick; else outline
                        current
                          ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                          : answered
                            ? 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/15'
                            : 'border-input bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground',
                      )}
                    >
                      {answered && !current ? (
                        <Check className="size-3.5" aria-hidden="true" />
                      ) : (
                        i + 1
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Question card — keyed on the question id so it re-runs the
                fade-in entrance every time the learner changes question. */}
            <div key={question.id} className="animate-fade-in">
              <QuestionCard
                attemptId={session.attemptId}
                question={question}
                number={index + 1}
                answer={answers[question.id] ?? EMPTY_ANSWER}
                onChange={(patch) => markAnswer(question.id, patch)}
                onAdvance={() => {
                  if (!isLast) goTo(index + 1);
                }}
              />
            </div>

            {submitError && !confirmOpen ? (
              <p
                className="flex items-center gap-2 text-sm font-medium text-destructive"
                role="alert"
              >
                <AlertCircle className="size-4 shrink-0" aria-hidden="true" />
                {submitError}
              </p>
            ) : null}

            {/* Footer nav — sticky to the viewport bottom so Next/Submit stay
                reachable while scrolling a long question (Claude Design language). */}
            <div className="sticky bottom-0 -mx-4 mt-auto flex items-center justify-between gap-3 border-t bg-background/80 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/70">
              <Button
                variant="outline"
                className="press min-h-touch"
                onClick={() => goTo(index - 1)}
                disabled={index === 0 || finishing}
                data-testid="quiz-prev"
              >
                <ChevronLeft aria-hidden="true" />
                Previous
              </Button>
              {isLast ? (
                <Button
                  className="press min-h-touch"
                  onClick={openSubmitConfirm}
                  disabled={finishing}
                  data-testid="quiz-submit"
                >
                  {finishing ? 'Submitting…' : 'Submit test'}
                </Button>
              ) : (
                <Button
                  className="press min-h-touch"
                  onClick={() => goTo(index + 1)}
                  disabled={finishing}
                  data-testid="quiz-next"
                >
                  Next
                  <ChevronRight aria-hidden="true" />
                </Button>
              )}
            </div>

            <ConfirmDialog
              open={confirmOpen}
              onOpenChange={(open) => {
                if (!finishing) setConfirmOpen(open);
              }}
              title="Submit your test?"
              description={`You answered ${answeredCount} of ${total} question${total === 1 ? '' : 's'}.${
                answeredCount < total ? ' Unanswered questions score zero.' : ''
              }`}
              confirmLabel="Submit test"
              cancelLabel="Keep working"
              pending={finishing}
              error={submitError}
              onConfirm={() => void handleSubmit('manual')}
            />
          </div>
        ) : null}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/** One labelled figure in the pre-quiz facts row. */
function Fact({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="rounded-lg border bg-card px-2 py-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold tabular-nums">{value}</p>
    </div>
  );
}

/** Whole minutes for a time limit, e.g. 600s → "10 min". */
function minutesLabel(seconds: number | null): string | null {
  if (seconds == null || seconds <= 0) return null;
  return `${Math.round(seconds / 60)} min`;
}

/** Exported for component tests (SCH-I4) — not used outside this route. */
export function IntroCard({
  quizId,
  title,
  starting,
  onStart,
}: {
  quizId: string;
  title: string | undefined;
  starting: boolean;
  onStart: () => void;
}): React.JSX.Element {
  // Ishraf §4: show what the student is about to sit BEFORE they commit —
  // chapters covered, how many questions, total marks, the time limit, and the
  // teacher's own instructions. Best-effort: if this fails the Start button
  // still works exactly as before.
  const intro = trpc.learning.quizzes.introForLearner.useQuery(
    { quizId },
    { retry: false, staleTime: 60_000 },
  );
  const d = intro.data ?? null;
  const limit = minutesLabel(d?.timeLimitSeconds ?? null);

  return (
    <div className="flex flex-1 items-center justify-center">
      <Card className="animate-scale-in w-full max-w-md">
        <CardHeader className="text-center">
          <h2 className="text-xl font-semibold">{d?.title ?? title ?? 'Ready when you are'}</h2>
          {d && d.chapters.length > 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="quiz-intro-chapters">
              {d.chapters.join(' · ')}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Take a breath — here&apos;s how it works.
            </p>
          )}
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          {d ? (
            <div className="grid grid-cols-3 gap-2 text-center" data-testid="quiz-intro-facts">
              <Fact label="Questions" value={d.questionCount > 0 ? String(d.questionCount) : '—'} />
              <Fact label="Marks" value={d.totalMarks > 0 ? String(d.totalMarks) : '—'} />
              <Fact label="Time" value={limit ?? 'No limit'} />
            </div>
          ) : null}

          {d?.instructions?.trim() ? (
            <div
              className="rounded-lg border border-primary/25 bg-primary/5 px-3 py-2.5 text-sm leading-6"
              data-testid="quiz-intro-instructions"
            >
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">
                From your teacher
              </p>
              <MathText text={d.instructions} />
            </div>
          ) : null}

          <ul className="flex flex-col gap-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <span className="text-primary" aria-hidden="true">
                ✓
              </span>
              Your answers save automatically as you go.
            </li>
            <li className="flex gap-2">
              <span className="text-primary" aria-hidden="true">
                ✓
              </span>
              Move back and forth between questions any time.
            </li>
            <li className="flex gap-2">
              <span className="text-primary" aria-hidden="true">
                ✓
              </span>
              Submit when you&apos;re done to see your score.
            </li>
          </ul>
          <Button
            size="lg"
            className="press w-full"
            onClick={onStart}
            disabled={starting}
            data-testid="quiz-begin"
          >
            {starting ? 'Starting…' : 'Start test'}
          </Button>
          <Link
            to="/catalog"
            className="text-center text-sm text-muted-foreground underline-offset-4 hover:underline"
          >
            Back to catalog
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}

function StartErrorCard({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}): React.JSX.Element {
  const code = errorCodeOf(error);
  const message = messageOf(error);
  const needsEnrollment = code === 'FORBIDDEN' && message.toLowerCase().includes('enrolled');
  return (
    <div className="flex flex-1 items-center justify-center">
      <Card className="animate-scale-in w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-4 py-8 text-center">
          <span className="flex size-10 items-center justify-center rounded-full bg-muted">
            <AlertCircle className="size-5 text-muted-foreground" aria-hidden="true" />
          </span>
          {needsEnrollment ? (
            <>
              <h2 className="text-lg font-semibold">You're not enrolled yet</h2>
              <p className="text-sm text-muted-foreground">
                You need to enroll in this course first — then come back and take the test.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold">Can't start this test</h2>
              <p className="text-sm text-muted-foreground">{message}</p>
            </>
          )}
          <div className="flex flex-col items-center gap-2">
            <Link
              to="/catalog"
              className={cn(buttonVariants({ variant: 'default' }), 'min-h-touch')}
            >
              Back to catalog
            </Link>
            {!needsEnrollment ? (
              <Button variant="ghost" size="sm" onClick={onRetry}>
                Try again
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CenteredCard({ title, body }: { title: string; body: string }): React.JSX.Element {
  return (
    <div className="flex flex-1 items-center justify-center">
      <Card className="animate-scale-in w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground">{body}</p>
          <Link to="/catalog" className={cn(buttonVariants({ variant: 'outline' }), 'min-h-touch')}>
            Back to catalog
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}

const TEXTAREA_CLASSES =
  'flex min-h-40 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * `<QuestionImage>` — render an image stored in `file_objects` next to a
 * question prompt or option (SAT-style figure questions, P-SCH-*).
 *
 * The bytes live as base64 in Postgres (no R2 / signed URL — the same MVP
 * storage as Drive, ADR-0090). A learner reads them through the learner-readable
 * `learning.attempts.getQuestionImage` proc (under the already-allowlisted
 * `learning.attempts.` prefix, see init.ts LEARNER_ALLOWED_PREFIXES) and we
 * build a `data:` URL — exactly the Drive preview pattern
 * (`apps/admin/.../drive/preview.tsx`). Defensive on every axis: the proc is
 * accessed by name off the `trpc` client (so a not-yet-shipped proc can't break
 * typecheck), and a missing id / load failure renders NOTHING (the prompt /
 * option text via <MathText> is always the source of truth).
 */
function QuestionImage({
  objectId,
  alt,
  className,
}: {
  objectId: string | null;
  alt: string;
  className?: string;
}): React.JSX.Element | null {
  // Probe the learner image proc defensively — typed loosely so this file
  // compiles before the regenerated contract carries the proc.
  const attempts = trpc.learning.attempts as unknown as {
    getQuestionImage?: {
      useQuery: (
        input: { objectId: string },
        opts: { enabled: boolean; staleTime: number },
      ) => { data?: { mediaType: string | null; dataBase64: string } };
    };
  };
  const query = attempts.getQuestionImage?.useQuery(
    { objectId: objectId ?? '' },
    { enabled: objectId != null, staleTime: Infinity },
  );
  if (objectId == null || !query?.data) return null;
  const { mediaType, dataBase64 } = query.data;
  return (
    <img
      src={`data:${mediaType ?? 'image/png'};base64,${dataBase64}`}
      alt={alt}
      className={cn(
        'max-h-80 max-w-full rounded-md border bg-background object-contain',
        className,
      )}
    />
  );
}

/**
 * `<HandwrittenWorkField>` — let a learner attach a PHOTO of their handwritten
 * work to a short_answer / essay question (P-SCH-D). The picked image is
 * base64-uploaded through `learning.attempts.uploadAnswerImage` (the same
 * file_objects / Drive base64-in-postgres path as question images), and the
 * returned `fileObjectId` is reported via `onChange` so it rides along in the
 * `saveAnswer` autosave as `answerImageObjectId`. The persisted bytes are read
 * back via `learning.attempts.getAnswerImage` and shown as a `data:` URL.
 *
 * Defensive on every axis (mirrors `<QuestionImage>`): both procs are accessed
 * BY NAME off the loosely-typed client so this compiles before the regenerated
 * `@mangrove/api-contract` rollup carries them; a not-yet-shipped proc / load
 * failure simply renders no preview — the typed answer is always the source of
 * truth.
 */
function HandwrittenWorkField({
  attemptId,
  questionId,
  value,
  onChange,
}: {
  attemptId: string;
  questionId: string;
  value: string | null;
  onChange: (fileObjectId: string | null) => void;
}): React.JSX.Element {
  const attempts = trpc.learning.attempts as unknown as {
    uploadAnswerImage?: {
      useMutation: () => {
        mutateAsync: (input: {
          attemptId: string;
          questionId: string;
          mediaType: string;
          dataBase64: string;
        }) => Promise<{ fileObjectId: string }>;
      };
    };
    getAnswerImage?: {
      useQuery: (
        input: { attemptId: string; questionId: string },
        opts: {
          enabled: boolean;
          staleTime: number | ((query: { state: { data: unknown } }) => number);
        },
      ) => { data?: { mediaType: string; dataBase64: string } | null };
    };
  };

  const upload = attempts.uploadAnswerImage?.useMutation();
  // A stored image only changes through a save, and the runner invalidates this
  // query after every save that carries one, so real bytes can stay fresh
  // forever. An EMPTY reply cannot: it may predate the autosave, and the learn
  // cache survives reloads in IndexedDB. Treat it as stale so a remount asks again.
  const imageQ = attempts.getAnswerImage?.useQuery(
    { attemptId, questionId },
    {
      enabled: value != null,
      staleTime: (query) => (query.state.data == null ? 0 : Infinity),
    },
  );
  const stored = value != null ? (imageQ?.data ?? null) : null;

  return (
    <div className="flex flex-col gap-2">
      <ImageUploadField
        label="Attach a photo of your work (optional)"
        value={value}
        onChange={onChange}
        disabled={upload == null}
        upload={async (dataBase64, mediaType) => {
          if (!upload) throw new Error('Image upload is unavailable.');
          const res = await upload.mutateAsync({ attemptId, questionId, mediaType, dataBase64 });
          return res.fileObjectId;
        }}
      />
      {stored ? (
        <img
          src={`data:${stored.mediaType};base64,${stored.dataBase64}`}
          alt="Your attached handwritten work"
          data-testid="answer-image-preview"
          className="max-h-80 max-w-full rounded-md border bg-background object-contain"
        />
      ) : null}
    </div>
  );
}

function QuestionCard({
  attemptId,
  question,
  number,
  answer,
  onChange,
  onAdvance,
}: {
  attemptId: string;
  question: LearnerQuestion;
  number: number;
  answer: LocalAnswer;
  onChange: (patch: Partial<LocalAnswer>) => void;
  onAdvance: () => void;
}): React.JSX.Element {
  const kind = kindOf(question.kind);
  const isRadio = kind === 'mcq' || kind === 'true_false';
  const options = [...question.options].sort((a, b) => a.position - b.position);

  return (
    <Card
      className="shadow-md"
      onKeyDown={
        isRadio
          ? (e) => {
              // Enter advances on single-choice screens (radios don't use Enter).
              if (e.key === 'Enter') {
                e.preventDefault();
                onAdvance();
              }
            }
          : undefined
      }
    >
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Question {number}
          </span>
          <Badge variant="secondary">
            {question.points} {question.points === 1 ? 'point' : 'points'}
          </Badge>
        </div>
        {question.passage ? (
          <div
            data-testid="question-passage"
            className="whitespace-pre-line rounded-md border-l-4 border-primary/40 bg-muted/40 p-2 text-sm leading-relaxed text-foreground"
          >
            <MathText text={question.passage} />
          </div>
        ) : null}
        <div
          data-testid="question-prompt"
          className="text-xl font-semibold leading-relaxed tracking-tight sm:text-2xl"
        >
          <MathText text={question.prompt} />
        </div>
        <QuestionImage
          objectId={imageObjectIdOf(question, 'promptImageObjectId')}
          alt={`Figure for question ${number}`}
          className="mt-1"
        />
      </CardHeader>
      <CardContent>
        {kind === 'mcq' || kind === 'true_false' || kind === 'multi' ? (
          <div
            role={kind === 'multi' ? 'group' : 'radiogroup'}
            aria-label={kind === 'multi' ? 'Select all that apply' : 'Choose one answer'}
            className="flex flex-col gap-2"
          >
            {kind === 'multi' ? (
              <p className="text-xs text-muted-foreground">Select all that apply.</p>
            ) : null}
            {options.map((option, oi) => {
              const checked = answer.selectedOptionIds.includes(option.id);
              // A/B/C/D… key cap (Claude Design quiz-runner language): a tactile
              // letter chip that fills primary when chosen, plus a check on the
              // right. The native input stays for keyboard + a11y but is sr-only.
              const letter = String.fromCharCode(65 + oi);
              return (
                <label
                  key={option.id}
                  data-testid="option-choice"
                  className={cn(
                    'press group flex min-h-touch cursor-pointer items-center gap-3.5 rounded-xl border p-3.5 text-sm transition-all duration-150 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 focus-within:ring-offset-background',
                    checked
                      ? 'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20'
                      : 'border-input hover:border-primary/40 hover:bg-primary/5',
                  )}
                >
                  <input
                    type={kind === 'multi' ? 'checkbox' : 'radio'}
                    name={`question-${question.id}`}
                    value={option.id}
                    checked={checked}
                    onChange={() => {
                      if (kind === 'multi') {
                        onChange({
                          selectedOptionIds: checked
                            ? answer.selectedOptionIds.filter((id) => id !== option.id)
                            : [...answer.selectedOptionIds, option.id],
                        });
                      } else {
                        onChange({ selectedOptionIds: [option.id] });
                      }
                    }}
                    className="sr-only"
                  />
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex size-8 shrink-0 items-center justify-center rounded-lg border text-xs font-bold transition-all duration-150',
                      checked
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-muted/40 text-muted-foreground group-hover:border-primary/40',
                    )}
                  >
                    {letter}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-2 leading-snug">
                    <MathText text={option.label} />
                    <QuestionImage
                      objectId={imageObjectIdOf(option, 'imageObjectId')}
                      alt={`Image for an answer choice on question ${question.id}`}
                      className="max-h-40"
                    />
                  </span>
                  <Check
                    aria-hidden="true"
                    className={cn(
                      'size-5 shrink-0 text-primary transition-all duration-200',
                      checked ? 'scale-100 opacity-100' : 'scale-50 opacity-0',
                    )}
                  />
                </label>
              );
            })}
          </div>
        ) : kind === 'numeric' ? (
          <Input
            type="number"
            inputMode="decimal"
            step="any"
            value={answer.numericRaw}
            onChange={(e) => onChange({ numericRaw: e.target.value })}
            placeholder="Enter a number"
            aria-label="Your answer (number)"
            className="max-w-xs"
          />
        ) : kind === 'essay' ? (
          <div className="flex flex-col gap-3">
            <textarea
              value={answer.text}
              onChange={(e) => onChange({ text: e.target.value })}
              placeholder="Write your answer…"
              aria-label="Your answer"
              className={TEXTAREA_CLASSES}
            />
            <HandwrittenWorkField
              attemptId={attemptId}
              questionId={question.id}
              value={answer.answerImageObjectId}
              onChange={(id) => onChange({ answerImageObjectId: id })}
            />
            <p className="text-xs text-muted-foreground">
              This answer will be graded by an instructor.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Input
              value={answer.text}
              onChange={(e) => onChange({ text: e.target.value })}
              placeholder={kind === 'fill_blank' ? 'Fill in the blank' : 'Type your answer'}
              aria-label="Your answer"
            />
            {kind === 'short_answer' ? (
              <>
                <HandwrittenWorkField
                  attemptId={attemptId}
                  questionId={question.id}
                  value={answer.answerImageObjectId}
                  onChange={(id) => onChange({ answerImageObjectId: id })}
                />
                <p className="text-xs text-muted-foreground">
                  This answer will be graded by an instructor.
                </p>
              </>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
